// useBookingBlocks — owns the derivation and mutation of the booking
// widget's per-property `blocks` array.
//
// Two operating modes:
//  • Uncontrolled (default) — reads from and writes to the shared
//    `BookingContext.selectedHotels` map. The widget then reflects
//    changes made anywhere else (BookingPage, StickySummary, …).
//  • Controlled — the caller passes `value` (array) + `onChange`.
//    Used by the /booking page's embedded planner where the parent
//    keeps its own copy of the enquiry rows.
//
// The hook returns the derived `blocks` array plus a set of stable
// mutation helpers (add, update, duplicate, remove, apply-dates-to-all
// and a low-level `mutate(fn)` that other hooks can compose on top of).
//
// Extracted from `HomeBookingWidget.jsx` — keeping the shape 1:1 with
// the original inline logic so behaviour is byte-identical.

import { useCallback, useMemo } from "react";
import { useBooking } from "@/contexts/BookingContext";

const cleanRoom = (r) => ({
  room_type: r?.room_type || "",
  adults: Number(r?.adults) || 2,
  children: Number(r?.children) || 0,
  infants: Number(r?.infants) || 0,
  board_basis: r?.board_basis || "breakfast",
});

const defaultRoom = () => cleanRoom({});

// Derive one row per selected slug (or per camp when the parent hotel
// carries `campBookings`). Each row gets a stable `rowId` so React
// keeps focus + input state when the parent re-renders.
const deriveCtxBlocks = (selectedHotels) => {
  const out = [];
  Object.entries(selectedHotels).forEach(([slug, det]) => {
    const buildRooms = (rooms, keyPrefix) =>
      Array.isArray(rooms) && rooms.length
        ? rooms.map((r, i) => ({ id: `${keyPrefix}-r${i}`, ...r }))
        : [
            {
              id: `${keyPrefix}-r0`,
              room_type: "",
              adults: 2,
              children: 0,
              infants: 0,
              board_basis: "breakfast",
            },
          ];
    if (Array.isArray(det?.campBookings) && det.campBookings.length > 0) {
      det.campBookings.forEach((cb, idx) => {
        const campSlug = cb?.campSlug || null;
        const rowId = `${slug}__${campSlug || "camp"}__${idx}`;
        out.push({
          rowId,
          slug,
          campSlug,
          campSlugs: campSlug ? [campSlug] : [],
          check_in: cb?.check_in || "",
          check_out: cb?.check_out || "",
          notes: cb?.notes || "",
          rooms: buildRooms(cb?.rooms, rowId),
        });
      });
      return;
    }
    out.push({
      rowId: slug,
      slug,
      campSlug: null,
      campSlugs: [],
      check_in: det?.check_in || "",
      check_out: det?.check_out || "",
      notes: det?.notes || "",
      rooms: buildRooms(det?.rooms, slug),
    });
  });
  return out;
};

export default function useBookingBlocks({ value, onChange } = {}) {
  const { selectedHotels, setSelectedHotels } = useBooking();
  const isControlled = Array.isArray(value);

  const ctxBlocks = useMemo(
    () => deriveCtxBlocks(selectedHotels),
    [selectedHotels]
  );
  const blocks = isControlled ? value : ctxBlocks;

  // Project a mutated `blocks` array back into the context
  // `selectedHotels` map. Camp-parent blocks (those with a `campSlug`)
  // are grouped into a `campBookings` array under their parent slug so
  // every camp keeps its own dates / rooms / notes intact.
  const projectBlocksToContext = useCallback(
    (nextBlocks) => {
      const out = {};
      for (const b of nextBlocks) {
        if (!b?.slug) continue;
        const cleanRooms =
          Array.isArray(b.rooms) && b.rooms.length
            ? b.rooms.map(cleanRoom)
            : [defaultRoom()];
        if (b.campSlug) {
          if (!out[b.slug]) out[b.slug] = { campBookings: [] };
          if (!Array.isArray(out[b.slug].campBookings)) {
            out[b.slug].campBookings = [];
          }
          out[b.slug].campBookings.push({
            campSlug: b.campSlug,
            check_in: b.check_in || "",
            check_out: b.check_out || "",
            notes: b.notes || "",
            rooms: cleanRooms,
          });
        } else if (!out[b.slug]) {
          out[b.slug] = {
            check_in: b.check_in || "",
            check_out: b.check_out || "",
            notes: b.notes || "",
            campSlugs: [],
            rooms: cleanRooms,
          };
        } else {
          // Two non-camp rows for the same slug → concat their rooms.
          out[b.slug].rooms = [...(out[b.slug].rooms || []), ...cleanRooms];
        }
      }
      setSelectedHotels(out);
    },
    [setSelectedHotels]
  );

  const mutate = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(blocks) : updater;
      if (isControlled) {
        onChange?.(next);
      } else {
        projectBlocksToContext(next);
      }
    },
    [isControlled, onChange, blocks, projectBlocksToContext]
  );

  const updateBlock = useCallback(
    (rowId, next) => mutate((cur) => cur.map((b) => (b.rowId === rowId ? next : b))),
    [mutate]
  );

  const duplicateBlock = useCallback(
    (rowId) =>
      mutate((cur) => {
        const src = cur.find((b) => b.rowId === rowId);
        if (!src) return cur;
        const idx = cur.indexOf(src);
        const copy = {
          ...src,
          rowId: `${src.slug}-${Date.now().toString(36)}-${Math.random()
            .toString(36)
            .slice(2, 6)}`,
        };
        const nextArr = [...cur];
        nextArr.splice(idx + 1, 0, copy);
        return nextArr;
      }),
    [mutate]
  );

  const removeBlock = useCallback(
    (rowId) => mutate((cur) => cur.filter((b) => b.rowId !== rowId)),
    [mutate]
  );

  const applyDatesToAll = useCallback(
    (rowId) => {
      const src = blocks.find((b) => b.rowId === rowId);
      if (!src) return;
      mutate((cur) =>
        cur.map((b) => ({ ...b, check_in: src.check_in, check_out: src.check_out }))
      );
    },
    [blocks, mutate]
  );

  return {
    blocks,
    isControlled,
    mutate,
    updateBlock,
    duplicateBlock,
    removeBlock,
    applyDatesToAll,
  };
}
